﻿// src/cockpit4/components/AIChatLogsPanel.tsx
// AI 对话记录 — 接入 /ai-visibility/logs 真实数据

import { useState, useEffect, useMemo } from "react";
import {
  Search as SearchIcon, Settings2, Download, RotateCcw, ArrowUpDown, Info,
} from "lucide-react";
import EmptyState, { MonitorFilterBar } from "./_MonitorEmptyState";

interface Props {
  dateStart: string;
  dateEnd: string;
  onDateChange: (s: string, e: string) => void;
  onAddBrand: () => void;
}

interface LogRecord {
  platform: string;
  question: string;
  answer: string;
  mentioned: boolean;
  firstPosition: boolean;
  top3: boolean;
  sources: string[];
  ownArticles: string[];
  time: string;
}

const PLATFORM_NAMES: Record<string, string> = {
  doubao: '豆包', ernie: '文心一言', qwen: '通义千问',
  yuanbao: '腾讯元宝', deepseek: 'DeepSeek', kimi: 'Kimi',
  baidu: '百度AI', wenxin: '文心',
};

export default function AIChatLogsPanel({ dateStart, dateEnd, onDateChange, onAddBrand }: Props) {
  const [records, setRecords] = useState<LogRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterSentiment, setFilterSentiment] = useState('all');
  const [searchKw, setSearchKw] = useState('');

  useEffect(() => {
    setLoading(true);
    fetch("/ai-visibility/logs?days=7")
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setRecords(d.records || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dateStart, dateEnd]);

  const filtered = useMemo(() => {
    let r = records;
    if (filterType === 'mentioned') r = r.filter(x => x.mentioned);
    else if (filterType === 'not-mentioned') r = r.filter(x => !x.mentioned);
    else if (filterType === 'top1') r = r.filter(x => x.firstPosition);
    else if (filterType === 'top3') r = r.filter(x => x.top3);
    if (searchKw) {
      const kw = searchKw.toLowerCase();
      r = r.filter(x => (x.question + x.answer + x.sources.join('')).toLowerCase().includes(kw));
    }
    return r;
  }, [records, filterType, searchKw]);

  return (
    <div className="min-h-full text-white">
      <div className="px-4 pt-4">
        <h2 className="text-[17px] font-bold text-white flex items-center gap-2">
          <span className="text-[14px]">💬</span> AI对话记录
          <span className="text-[12px] text-gray-500 font-normal">（共 {records.length} 条，筛选后 {filtered.length} 条）</span>
        </h2>
      </div>

      <MonitorFilterBar onAddBrand={onAddBrand}
        dateStart={dateStart} dateEnd={dateEnd} onDateChange={onDateChange} />
      <div className="px-4">
        <div className="h-[7px] bg-gradient-to-r from-violet-500/30 via-cyan-500/20 to-violet-500/30 rounded-full mb-4" />

        {/* 对话记录工具栏 */}
        <div className="bg-black/30 rounded-2xl border border-white/[0.08] overflow-hidden">
          <div className="flex items-center justify-between flex-wrap gap-2 px-5 py-3 border-b border-slate-100">
            <div className="text-[14px] font-semibold text-gray-200">对话记录</div>
            <div className="flex items-center gap-2 flex-wrap">
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-1.5 rounded-md text-[12px] border border-white/[0.08] text-gray-200 bg-black/30 min-w-[120px]">
                <option value="all">全部对话</option>
                <option value="mentioned">已提及品牌</option>
                <option value="not-mentioned">未提及品牌</option>
                <option value="top1">Top1提及</option>
                <option value="top3">Top3提及</option>
              </select>
              <select value={filterSentiment} onChange={(e) => setFilterSentiment(e.target.value)}
                className="px-3 py-1.5 rounded-md text-[12px] border border-white/[0.08] text-gray-200 bg-black/30 min-w-[130px]">
                <option value="all">全部情感倾向</option>
                <option value="pos">正面</option>
                <option value="neu">中性</option>
                <option value="neg">负面</option>
              </select>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/[0.08] bg-black/30 min-w-[260px]">
                <SearchIcon size={13} className="text-gray-500" />
                <input placeholder="搜索品牌、竞品、其他品牌关键词" value={searchKw}
                  onChange={(e) => setSearchKw(e.target.value)}
                  className="bg-transparent outline-none text-[12px] text-gray-200 w-full" />
              </div>
              <button className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px]
                                 bg-black/30 border border-white/[0.08] text-white font-semibold hover:text-violet-300 hover:border-violet-400/40">
                <Settings2 size={13} /> 列表配置
              </button>
              <button className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px]
                                 bg-black/30 border border-white/[0.08] text-white font-semibold hover:text-violet-300 hover:border-violet-400/40">
                <Download size={13} /> 导出
              </button>
              <button onClick={() => { setFilterType('all'); setSearchKw(''); }}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px]
                                 bg-black/30 border border-white/[0.08] text-gray-400 hover:text-white hover:border-violet-400/40">
                <RotateCcw size={13} /> 重置
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px] border-collapse">
              <thead>
                <tr className="bg-black/30 text-gray-400 text-[11.5px]">
                  <th className="text-left p-3 rounded-l-lg">AI问题 <ArrowUpDown size={10} className="inline" /></th>
                  <th className="text-left p-3">对话时间 <ArrowUpDown size={10} className="inline text-violet-400" /></th>
                  <th className="text-left p-3">对话平台 <ArrowUpDown size={10} className="inline" /></th>
                  <th className="text-left p-3">问题类型</th>
                  <th className="text-right p-3">提及品牌</th>
                  <th className="text-right p-3">排名 <ArrowUpDown size={10} className="inline" /></th>
                  <th className="text-left p-3">引用来源 <ArrowUpDown size={10} className="inline" /></th>
                  <th className="text-center p-3 rounded-r-lg">操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? (
                  filtered.slice(0, 100).map((r, i) => (
                    <tr key={i} className="border-t border-white/[0.04] hover:bg-white/[0.02]">
                      <td className="p-3 text-gray-200 max-w-[200px] truncate" title={r.question}>{r.question}</td>
                      <td className="p-3 text-gray-400 whitespace-nowrap">{r.time || '—'}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${
                          r.mentioned ? 'bg-green-500/15 text-green-400' : 'bg-black/30 text-gray-400'
                        }`}>
                          {PLATFORM_NAMES[r.platform] || r.platform}
                        </span>
                      </td>
                      <td className="p-3 text-gray-400">—</td>
                      <td className="p-3 text-right">
                        {r.mentioned ? (
                          <span className="text-green-400 font-semibold">✓ 是</span>
                        ) : (
                          <span className="text-gray-500">—</span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        {r.firstPosition ? <span className="text-violet-400 font-bold">Top1</span> :
                         r.top3 ? <span className="text-cyan-400 font-semibold">Top3</span> :
                         r.mentioned ? <span className="text-gray-400">已提及</span> :
                         <span className="text-gray-600">—</span>}
                      </td>
                      <td className="p-3 text-gray-400 max-w-[200px] truncate" title={(r.sources || []).join(', ')}>
                        {(r.sources || []).join(', ') || '—'}
                      </td>
                      <td className="p-3 text-center">
                        <button className="text-[11px] text-violet-400 hover:text-violet-300">查看</button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8}>
                      <EmptyState text={loading ? "加载中..." : "暂无AI对话记录"} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {filtered.length > 100 && (
              <div className="text-center py-3 text-[12px] text-gray-500">
                仅显示前 100 条，共 {filtered.length} 条，请缩小搜索范围
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
